import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Collapse,
} from '@mui/material';
import type { TrainingFormData } from '../types';
import { RACE_DISTANCES } from '../constants';

interface TrainingFormProps {
  onSubmit: (data: TrainingFormData) => void;
  initialData?: TrainingFormData | null;
}

export const TrainingForm: React.FC<TrainingFormProps> = ({ onSubmit, initialData }) => {
  const today = new Date().toISOString().split('T')[0];

  const [distance, setDistance] = useState(initialData?.distance || RACE_DISTANCES[0]);
  const [otherDistance, setOtherDistance] = useState('');
  const [goalTime, setGoalTime] = useState(initialData?.goalTime || '');
  const [raceDate, setRaceDate] = useState(initialData?.raceDate || '');
  const [startDate, setStartDate] = useState(initialData?.startDate || today);
  const [weeks, setWeeks] = useState<number | string>(initialData?.weeks || '');
  const [showOptional, setShowOptional] = useState(false);
  const [recent5k, setRecent5k] = useState(initialData?.recent5k || '');
  const [recent10k, setRecent10k] = useState(initialData?.recent10k || '');
  const [recentHalf, setRecentHalf] = useState(initialData?.recentHalf || '');
  const [recentFull, setRecentFull] = useState(initialData?.recentFull || '');

  useEffect(() => {
    if (weeks && startDate) {
      const numWeeks = Number(weeks);
      if (numWeeks >= 2 && numWeeks <= 30) {
        const newRaceDate = new Date(startDate);
        newRaceDate.setDate(newRaceDate.getDate() + (numWeeks * 7));
        setRaceDate(newRaceDate.toISOString().split('T')[0]);
      }
    }
  }, [weeks, startDate]);

  const handleRaceDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRaceDate(e.target.value);
    setWeeks('');
  };

  const handleWeeksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 2 && Number(value) <= 30)) {
      setWeeks(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Stricter validation
    if (!goalTime.trim()) {
      alert('Please provide a Goal Time.');
      return;
    }
    if (!raceDate && (!weeks || Number(weeks) < 2)) {
      alert('Please either select a Race Date or enter a valid number of weeks (2-30).');
      return;
    }
    if (distance === 'Other' && !otherDistance.trim()) {
      alert('Please specify the distance for your "Other" race.');
      return;
    }

    onSubmit({
      distance: distance === 'Other' ? otherDistance : distance,
      goalTime,
      raceDate,
      startDate,
      weeks,
      recent5k,
      recent10k,
      recentHalf,
      recentFull,
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><FormControl fullWidth><InputLabel>Target Distance</InputLabel><Select value={distance} label="Target Distance" onChange={(e) => setDistance(e.target.value)}>{RACE_DISTANCES.map((d) => (<MenuItem key={d} value={d}>{d}</MenuItem>))}</Select></FormControl></Grid>
        <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Goal Time" value={goalTime} onChange={(e) => setGoalTime(e.target.value)} placeholder="e.g., 3:45:00" required /></Grid>
        <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required InputLabelProps={{ shrink: true }} /></Grid>
        <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Weeks of Training" type="number" value={weeks} onChange={handleWeeksChange} inputProps={{ min: 2, max: 30 }} /></Grid>
        <Grid item xs={12}><Typography sx={{ textAlign: 'center', my: 1 }}>OR</Typography></Grid>
        <Grid item xs={12}><TextField fullWidth label="Race Date" type="date" value={raceDate} onChange={handleRaceDateChange} required={!weeks} InputLabelProps={{ shrink: true }} inputProps={{ min: startDate }} disabled={!!weeks} /></Grid>
        {distance === 'Other' && (<Grid item xs={12}><TextField fullWidth label="Specify Distance" value={otherDistance} onChange={(e) => setOtherDistance(e.target.value)} placeholder="e.g., 50k Ultra" required /></Grid>)}
        <Grid item xs={12}><Button onClick={() => setShowOptional(!showOptional)}>{showOptional ? 'Hide' : 'Show'} Optional Fields</Button></Grid>
        <Grid item xs={12}><Collapse in={showOptional}><Grid container spacing={2}><Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Recent 5k Time" value={recent5k} onChange={(e) => setRecent5k(e.target.value)} /></Grid><Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Recent 10k Time" value={recent10k} onChange={(e) => setRecent10k(e.target.value)} /></Grid><Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Recent Half Time" value={recentHalf} onChange={(e) => setRecentHalf(e.target.value)} /></Grid><Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Recent Full Time" value={recentFull} onChange={(e) => setRecentFull(e.target.value)} /></Grid></Grid></Collapse></Grid>
        <Grid item xs={12} sx={{ textAlign: 'center' }}><Button type="submit" variant="contained" size="large">Generate My Plan</Button></Grid>
      </Grid>
    </Box>
  );
};
